import { Fragment } from 'react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { Brand } from './Brand';
import { Chip } from './Chip';
import { Avatar } from './Card';

/**
 * BurgerMenu — the phone's ☰ (`maie.css` .burger), shared by all nine canvases.
 *
 * README §5: navigation is the fourth deliberate phone/desk divergence — the
 * phone has this, the desk has the 220px Rail. They are siblings, not
 * breakpoints of one component.
 *
 * §Doctor 03 [1]: "THE ☰ PERSISTS IN BOTH MODES, and HOME is always its first
 * item, so home from anywhere is one tap without spending a bar slot on a
 * dedicated button." It also holds appearance and feedback — there is no global
 * gear, because Settings is manager-only.
 *
 * Like the rail's account menu (§13.3), this is built ONCE and configured per
 * role rather than re-specced nine times. What varies: the doors, the role
 * chip, the extra-role group, and the foot identity.
 *
 * `note` is the part callers skip and shouldn't. The Doctor's reads: "The file,
 * the encounter, the sheet and the results open from a patient or from the
 * day's chips — never from here." A menu that lists everything teaches nothing;
 * saying what does NOT open from here is what makes the model learnable.
 */

export interface MenuDoor {
  key: string;
  icon: IconName;
  label: string;
  /** The one line under the door — a live count, not a description. */
  sub?: string;
  /** Right-hand marker, e.g. the "Home" chip on the current door. */
  badge?: ReactNode;
  /**
   * A heading drawn above this door when it differs from the door before —
   * the accountant's "Money in · Money out · The books".
   */
  section?: string;
  onSelect: () => void;
}

/**
 * A folded extra-role group. §10's corrected multi-role law: the PRIMARY role's
 * doors stay ungrouped at the top and the second hat folds beneath.
 */
export interface MenuGroup {
  head: string;
  /** The doors, as one summary line while folded. */
  summary: string;
  /** e.g. "5 doors · folded — the head doctor's other hat" */
  caption: string;
  items: Array<{ key: string; label: string; onSelect: () => void }>;
}

interface BurgerMenuProps {
  open: boolean;
  onClose: () => void;
  doors: MenuDoor[];
  /** e.g. "Doctor · Consultant". */
  roleChip?: string;
  group?: MenuGroup;
  groupOpen?: boolean;
  onToggleGroup?: () => void;
  /** What does NOT open from here. See the note above. */
  note?: ReactNode;
  account?: { name: string; initials: string; caption?: string; onSelect: () => void };
  branch?: string;
  onBranch?: () => void;
  onAppearance?: () => void;
  onFeedback?: () => void;
  onSignOut?: () => void;
  className?: string;
}

export function BurgerMenu({
  open,
  onClose,
  doors,
  roleChip,
  group,
  groupOpen = false,
  onToggleGroup,
  note,
  account,
  branch = 'Maadi ▾',
  onBranch,
  onAppearance,
  onFeedback,
  onSignOut,
  className,
}: BurgerMenuProps) {
  if (!open) return null;

  return (
    <div className={clsx('burger', className)} role="dialog" aria-modal="true" aria-label="Menu">
      <div
        className="topstrip"
        style={{
          margin: '0 calc(var(--m) * -1)',
          paddingInline: 'var(--m)',
          background: 'var(--surface-50)',
        }}
      >
        <button type="button" className="ts-btn" onClick={onClose} aria-label="Close menu">
          <Icon name="x" size="lg" />
        </button>
        <Brand mark="type" style={{ height: 16, width: 'auto', color: 'var(--ink-900)' }} />
        <span className="grow" />
        {roleChip ? <Chip kind="soft" small>{roleChip}</Chip> : null}
      </div>

      <div style={{ height: 12 }} />

      {doors.map((door, i) => (
        <Fragment key={door.key}>
          {door.section && door.section !== doors[i - 1]?.section ? (
            <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>{door.section}</p>
          ) : null}
          <button type="button" className="row w-full text-start" onClick={door.onSelect}>
            <Icon name={door.icon} size="lg" />
            <div className="grow">
              <p className="t-strong">{door.label}</p>
              {door.sub ? <p className="t-cap mut">{door.sub}</p> : null}
            </div>
            {door.badge}
          </button>
        </Fragment>
      ))}

      {group ? (
        <>
          <button type="button" className="menugroup-head w-full" onClick={onToggleGroup}>
            <p className="t-ovl mut grow">{group.head}</p>
            <span className="t-cap mut">{groupOpen ? '▾' : '▸'}</span>
          </button>

          {!groupOpen ? (
            <button
              type="button"
              className="row w-full text-start"
              style={{ background: 'var(--surface-100)', borderColor: 'transparent' }}
              onClick={onToggleGroup}
            >
              <div className="grow">
                <p className="t-sec w6">{group.summary}</p>
                <p className="t-cap mut">{group.caption}</p>
              </div>
            </button>
          ) : (
            group.items.map((item) => (
              <button key={item.key} type="button" className="row w-full text-start" onClick={item.onSelect}>
                <p className="t-strong grow">{item.label}</p>
                <Icon name="chevron-right" />
              </button>
            ))
          )}
        </>
      ) : null}

      {note ? (
        <div className="card card-ghost" style={{ marginTop: 8 }}>
          <p className="t-sec mut">{note}</p>
        </div>
      ) : null}

      {account ? (
        <button
          type="button"
          className="row w-full text-start"
          style={{ marginTop: 12 }}
          onClick={account.onSelect}
        >
          <Avatar initials={account.initials} />
          <div className="grow">
            <p className="t-strong">{account.name}</p>
            {account.caption ? <p className="t-cap mut">{account.caption}</p> : null}
          </div>
        </button>
      ) : null}

      {onAppearance ? (
        <button type="button" className="row w-full text-start" onClick={onAppearance}>
          <Icon name="adjustments" size="lg" />
          <div className="grow">
            <p className="t-strong">Appearance</p>
            <p className="t-cap mut">EN / AR · dark · text size · RTL · contrast · motion</p>
          </div>
        </button>
      ) : null}

      {onFeedback ? (
        <button type="button" className="row w-full text-start" onClick={onFeedback}>
          <Icon name="message-report" size="lg" />
          <p className="t-strong grow">Feedback</p>
        </button>
      ) : null}

      {onBranch || onSignOut ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {onBranch ? (
            <button type="button" className="row grow" style={{ margin: 0 }} onClick={onBranch}>
              <Icon name="building" />
              <p className="t-sec w6 grow">{branch}</p>
            </button>
          ) : null}
          {onSignOut ? (
            <button type="button" className="row grow" style={{ margin: 0 }} onClick={onSignOut}>
              <Icon name="logout" />
              <p className="t-sec w6 sec grow">Sign out</p>
            </button>
          ) : null}
        </div>
      ) : null}

      <div style={{ height: 24 }} />
    </div>
  );
}
